import { CommonModule } from '@angular/common';
import { Component, inject, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MAT_BOTTOM_SHEET_DATA,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BottomSheetTitleComponent } from 'src/app/components/bottom-sheet-title/bottom-sheet-title.component';
import { PeriodService } from 'src/app/services/period.service';
import { AuthService } from 'src/services/auth/auth.service';
import { DetailedTag } from 'src/services/tag/DetailedTag';
import { Summary } from 'src/services/tag/Tag';
import { TagService } from 'src/services/tag/tag.service';
import { duplicateSelected } from 'src/utils/duplicateSelected';
import { getTagSummary } from 'src/utils/getTagSummary';
import { getTopLevelTag } from 'src/utils/getTopLevelTag';
import { hasChildren } from 'src/utils/hasChildren';
import { isRoot } from 'src/utils/isRoot';
import { BottomSheetControlsComponent } from '../../app/bottom-sheet-controls/bottom-sheet-controls.component';
import { PeriodChooserComponent } from '../../app/components/period-chooser/period-chooser.component';

@Component({
  selector: 'edit-tag-bottom-sheet',
  styleUrl: './edit-tag-bottom-sheet.component.scss',
  templateUrl: 'edit-tag-bottom-sheet.component.html',
  standalone: true,
  imports: [
    CommonModule,
    MatListModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    BottomSheetControlsComponent,
    BottomSheetTitleComponent,
    FormsModule,
    PeriodChooserComponent,
  ],
})
export class EditTagBottomSheet {
  isRoot = isRoot;
  getTopLevelTag = getTopLevelTag;

  public _bottomSheetRef =
    inject<MatBottomSheetRef<EditTagBottomSheet>>(MatBottomSheetRef);

  newTag: DetailedTag = { ...this.data.tag };

  tags = this.data.tags;

  summary = Summary;
  getTagSummary = getTagSummary;

  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA)
    public data: {
      tag: DetailedTag;
      tags: DetailedTag[];
      archivedTags: DetailedTag[];
    },
    private tagService: TagService,
    private _snackBar: MatSnackBar,
    public authService: AuthService,
    private periodService: PeriodService
  ) {}

  updateTag() {
    if (this.hasProFeatures() && !this.authService.isPro) {
      this._bottomSheetRef.dismiss(true);
    } else {
      this.newTag.isPeriodic = !!this.newTag.isPeriodic;
      const validationErrorMessage = this.getValidationErrorMessage(
        this.newTag
      );

      if (validationErrorMessage === null) {
        this.tagService.updateById(this.newTag);
        this._snackBar.open('Tag updated', 'Dismiss');
        this._bottomSheetRef.dismiss();
      } else {
        this._snackBar.open(validationErrorMessage);
      }
    }
  }

  getValidationErrorMessage(tag: DetailedTag): string | null {
    if (tag.plannedExpenditure && tag.plannedExpenditure < 0) {
      return 'Planned expenditure cannot be negative';
    }
    if (tag.plannedIncome && tag.plannedIncome! < 0) {
      return 'Planned income cannot be negative';
    }
    if (tag.fullPath === '' || tag.fullPath.endsWith('/')) {
      return 'Empty tags are not allowed';
    }
    if (this.hasDuplicate()) {
      return 'Duplicate tags are not allowed';
    }
    if (tag.fullPath.split('/').slice(-1)[0].startsWith('-')) {
      return 'Tags cannot start with -';
    }
    if (tag.fullPath.split('/').slice(-1)[0].endsWith('-')) {
      return 'Tags cannot end with -';
    }
    return null;
  }

  hasDuplicate() {
    const tagPaths = this.tags.map((t) => t.fullPath);
    return new Set(tagPaths).size !== tagPaths.length;
  }

  updateName(tag: DetailedTag, event: Event) {
    const target = event.target as HTMLInputElement;
    const newPath = target.value.replace(' ', '-').toLowerCase();

    tag.fullPath = newPath;
  }

  deleteTag() {
    const confirmed = confirm('Are you sure you want to delete this tag?');
    if (confirmed && this.newTag.id) {
      this.tagService.deleteById(this.newTag.id);
      this._snackBar.open('Tag deleted', 'Dismiss');
      this._bottomSheetRef.dismiss();
    }
  }

  archiveTag(setToArchiveTrue = true) {
    if (!this.authService.isPro) {
      this._bottomSheetRef.dismiss(true);
    } else if (this.newTag.id) {
      this.tagService.archiveWithTree(this.newTag, setToArchiveTrue);
      this._snackBar.open('Tag archived', 'Dismiss');
      this._bottomSheetRef.dismiss();
    }
  }

  async duplicateTag() {
    if (hasChildren(this.data.tags, this.newTag)) {
      const confirmed = confirm('Duplicate tag with all its children?');
      if (!confirmed) {
        return;
      }
    }
    const tagsWithDuplicates = duplicateSelected(
      this.newTag,
      this.data.tags,
      this.tagService
    );
    await this.tagService.save([
      ...tagsWithDuplicates,
      ...this.data.archivedTags,
    ]);
    this._snackBar.open('Tag duplicated', 'Dismiss');
    this._bottomSheetRef.dismiss();
  }

  get plannedExpenditure() {
    if (!getTopLevelTag(this.newTag, this.data.tags)?.isPeriodic) {
      return this.newTag.plannedExpenditure;
    } else {
      const period = this.newTag.periods?.find(
        (p) =>
          p.period.month === this.periodService.current.month &&
          p.period.year === this.periodService.current.year
      );
      if (period && period.plannedExpenditure) {
        return period.plannedExpenditure;
      } else {
        return null;
      }
    }
  }

  set plannedExpenditure(val: any) {
    if (!getTopLevelTag(this.newTag, this.data.tags)?.isPeriodic) {
      this.newTag.plannedExpenditure = val;
    } else {
      const period = this.newTag.periods?.find(
        (p) =>
          p.period.month === this.periodService.current.month &&
          p.period.year === this.periodService.current.year
      );
      if (period) {
        period.plannedExpenditure = val;
      } else {
        this.newTag.periods?.push({
          period: this.periodService.current,
          plannedExpenditure: val,
        });
      }
    }
  }

  get plannedIncome() {
    if (!getTopLevelTag(this.newTag, this.data.tags)?.isPeriodic) {
      return this.newTag.plannedIncome;
    } else {
      const period = this.newTag.periods?.find(
        (p) =>
          p.period.month === this.periodService.current.month &&
          p.period.year === this.periodService.current.year
      );
      if (period && period.plannedIncome) {
        return period.plannedIncome;
      } else {
        return null;
      }
    }
  }

  set plannedIncome(val: any) {
    if (!getTopLevelTag(this.newTag, this.data.tags)?.isPeriodic) {
      this.newTag.plannedIncome = val;
    } else {
      const period = this.newTag.periods?.find(
        (p) =>
          p.period.month === this.periodService.current.month &&
          p.period.year === this.periodService.current.year
      );
      if (period) {
        period.plannedIncome = val;
      } else {
        this.newTag.periods?.push({
          period: this.periodService.current,
          plannedIncome: val,
        });
      }
    }
  }

  hasProFeatures() {
    return (
      this.newTag.isPeriodic ||
      !this.authService.FREE_LIMITATIONS.availableColors.includes(
        this.newTag.color
      )
    );
  }

  getSubmitText() {
    if (this.hasProFeatures() && !this.authService.isPro) {
      return 'Get Pro';
    } else {
      return 'Update';
    }
  }
}
